import dayjs from '@/dayjs'

export const DateLocale = Object.freeze({
    ENGLISH_AUSTRALIA: 'en_AU',
    ENGLISH_UNITED_STATES: 'en_US',
    ENGLISH_UNITED_KINGDOM: 'en_GB',
})

const DAYJS_LOCALE_MAP = {
    [DateLocale.ENGLISH_AUSTRALIA]: 'en-au',
    [DateLocale.ENGLISH_UNITED_STATES]: 'en',
    [DateLocale.ENGLISH_UNITED_KINGDOM]: 'en-gb',
}

// Logic for this class should be kept aligned with the API
// JS: src/support/DateHelper.js
// PHP counterpart: app/Support/DateHelper.php
export class DateHelper {
    /** @type {string} */
    #locale

    /** @type {string} */
    #appTimezone

    /** @type {string|null} */
    #deviceTimezone

    /** @type {boolean} */
    #sameTimezone

    // Default formats are for en_AU
    #dayFormat = 'dddd'       // Thursday
    #dateFormat = 'D MMMM'    // 5 January
    #timeFormat = 'h:mm a'    // 5:04 pm
    #yearFormat = 'YYYY'      // 2023
    #appendYearSymbol = ''    // (none)

    /**
   * @param {string} locale - One of DateLocale values
   * @param {string} appTimezone - The application (school) timezone, e.g. 'Australia/Melbourne'
   * @param {string|null} deviceTimezone - The device timezone, e.g. 'America/New_York'
   */
    constructor(locale, appTimezone, deviceTimezone = null) {
        this.#locale = locale
        this.#appTimezone = appTimezone
        this.#deviceTimezone = deviceTimezone
        this.#sameTimezone = appTimezone === deviceTimezone

        switch (locale) {
            case DateLocale.ENGLISH_UNITED_STATES:
                this.#dateFormat = 'MMMM D'  // January 5
                this.#appendYearSymbol = ','
                break
            case DateLocale.ENGLISH_UNITED_KINGDOM:
                this.#timeFormat = 'HH:mm'   // 17:04
                break
        }
    }

    /** @returns {string} */
    get #dayjsLocale() {
        return DAYJS_LOCALE_MAP[this.#locale] ?? 'en'
    }

    /** @returns {string} */
    get #effectiveTimezone() {
        return this.#deviceTimezone ?? this.#appTimezone
    }

    /**
   * Returns a new DateHelper with short format strings.
   *
   * @returns {DateHelper}
   */
    short() {
        const helper = this.#clone()
        helper.#dayFormat = 'ddd'

        switch (helper.#locale) {
            case DateLocale.ENGLISH_UNITED_STATES:
                helper.#dateFormat = 'MMM D'  // Jan 5
                break
            case DateLocale.ENGLISH_AUSTRALIA:
            case DateLocale.ENGLISH_UNITED_KINGDOM:
                helper.#dateFormat = 'D MMM'  // 5 Jan
                break
        }

        return helper
    }

    /**
   * @param {import('dayjs').Dayjs} date Date already in the effective timezone
   * @returns {string}
   */
    #timezoneSuffix(date) {
        if (this.#sameTimezone) {
            return ''
        }

        const friendlyTimezoneName = new Intl.DateTimeFormat(this.#dayjsLocale, {
            timeZone: this.#effectiveTimezone,
            timeZoneName: 'short',
        })
            .formatToParts(date)
            .find((part) => part.type === 'timeZoneName')
            ?.value

        return ` (${friendlyTimezoneName ?? this.#effectiveTimezone})`
    }

    /**
   * @param {boolean} condition
   * @param {import('dayjs').Dayjs} date Date already in the effective timezone
   * @returns {string}
   */
    #timezoneSuffixIf(condition, date) {
        return condition ? this.#timezoneSuffix(date) : ''
    }

    /**
   * Build a formatted date string from individual components.
   * If the user is in a different timezone to the school then we show them the time in their current timezone
   * and include the timezone abbreviation to make it clear it's not the time at the schools location
   *
   * @param {import('dayjs').Dayjs} date
   * @param {object} options
   * @param {boolean} [options.withDay=false]
   * @param {boolean} [options.withDate=false]
   * @param {boolean} [options.withYear=false]
   * @param {boolean} [options.withTime=false]
   * @param {boolean} [options.showTimezone=true]
   * @returns {string}
   */
    #buildDate(date, { withDay = false, withDate = false, withYear = false, withTime = false, showTimezone = true } = {}) {
        // Ensure the date is in the effective timezone
        const dateInTz = date.clone()
            .locale(this.#dayjsLocale)
            .tz(this.#effectiveTimezone)

        // Always show year if displaying date and not in the same year
        if (withDate && !dateInTz.isSame(this.now(), 'year')) {
            withYear = true
        }

        const format = [
            withDay ? this.#dayFormat + (withDate ? ',' : '') : null,
            withDate ? this.#dateFormat + (withYear ? this.#appendYearSymbol : '') : null,
            withYear ? this.#yearFormat : null,
            withTime ? this.#timeFormat : null,
        ]
            .filter(Boolean)
            .join(' ')

        return dateInTz.format(format) + this.#timezoneSuffixIf(showTimezone, dateInTz)
    }

    /**
   * @param {import('dayjs').Dayjs} date
   * @param {boolean} [showTimezone=true]
   * @returns {string}
   */
    formatTime(date, showTimezone = true) {
        return this.#buildDate(date, { withTime: true, showTimezone })
    }

    /**
   * @param {import('dayjs').Dayjs} date
   * @param {boolean} [showTimezone=true]
   * @param {boolean} [withYear=false]
   * @returns {string}
   */
    formatDate(date, showTimezone = true, withYear = false) {
        return this.#buildDate(date, { withDate: true, withYear, showTimezone })
    }

    /**
   * @param {import('dayjs').Dayjs} date
   * @param {boolean} [showTimezone=true]
   * @returns {string}
   */
    formatDayDate(date, showTimezone = true) {
        return this.#buildDate(date, { withDay: true, withDate: true, showTimezone })
    }

    /**
   * @param {import('dayjs').Dayjs} date
   * @param {boolean} [showTimezone=true]
   * @returns {string}
   */
    formatDateTime(date, showTimezone = true) {
        return this.#buildDate(date, { withDay: true, withDate: true, withTime: true, showTimezone })
    }

    /**
   * @param {import('dayjs').Dayjs} start
   * @param {import('dayjs').Dayjs} end
   * @param {boolean} [showTimezone=true]
   * @returns {string}
   */
    formatTimeRange(start, end, showTimezone = true) {
        if (start.hour() === end.hour() && start.minute() === end.minute()) {
            return this.formatTime(start, showTimezone)
        }

        return `${this.formatTime(start, false)} - ${this.formatTime(end, showTimezone)}`
    }

    /**
   * @param {import('dayjs').Dayjs} start
   * @param {import('dayjs').Dayjs} end
   * @param {boolean} [showTimezone=true]
   * @returns {string}
   */
    formatDateRange(start, end, showTimezone = true) {
        const isSameDay = start.isSame(end, 'day')
        const isSameYear = start.isSame(this.now(), 'year') && end.isSame(this.now(), 'year')

        if (isSameDay) {
            return this.formatDate(start, showTimezone)
        }

        return `${this.formatDate(start, false, !isSameYear)} - ${this.formatDate(end, showTimezone, !isSameYear)}`
    }

    /**
   * @param {import('dayjs').Dayjs} start
   * @param {import('dayjs').Dayjs} end
   * @param {boolean} [showTimezone=true]
   * @returns {string}
   */
    formatDateTimeRange(start, end, showTimezone = true) {
        const isSameDay = start.isSame(end, 'day')
        const isSameMinute = start.isSame(end, 'minute')

        if (isSameDay) {
            if (isSameMinute) {
                // Collapse to a single datetime
                return this.formatDateTime(start, showTimezone)
            }
            return this.formatDateTime(start, false) + ` - ${this.formatTime(end, showTimezone)}`
        }

        return `${this.formatDateTime(start, false)} - ${this.formatDateTime(end, showTimezone)}`
    }

    /**
   * Format an event's time display.
   *
   * @param {boolean} allDay
   * @param {import('dayjs').Dayjs} start
   * @param {import('dayjs').Dayjs} end
   * @returns {string}
   */
    formatEvent(allDay, start, end) {
        if (allDay) {
            return 'All Day'
        }

        return start.isSame(end, 'day')
            ? this.formatTimeRange(start, end)
            : this.short().formatDateTimeRange(start, end)
    }

    /**
     * Get the current moment in the device timezone.
     *
     * @returns {import('dayjs').Dayjs}
     */
    now() {
        return dayjs().tz(this.#effectiveTimezone).locale(this.#dayjsLocale)
    }

    /**
   * Check if a date is within the last 30 seconds.
   *
   * @param {import('dayjs').Dayjs} date
   * @param {import('dayjs').Dayjs|null} [now=null]
   * @returns {boolean}
   */
    isMomentsAgo(date, now = null) {
        const diff = (now ?? this.now()).diff(date, 'second')

        return diff >= 0 && diff < 30
    }

    /**
   * Get a human-readable relative time string (e.g. "5 minutes ago").
   *
   * @param {import('dayjs').Dayjs} date
   * @returns {string}
   */
    ago(date) {
        return date.locale(this.#dayjsLocale).from(this.now())
    }

    /**
   * Format a date/time relative to now if within the given period,
   * otherwise use the full date/time format.
   *
   * @param {import('dayjs').Dayjs} date
   * @param {[import('dayjs').Dayjs, import('dayjs').Dayjs]|null} [relativePeriod=null]
   * @returns {string}
   */
    formatDateTimeRelative(date, relativePeriod = null) {
        if (!relativePeriod) {
            relativePeriod = [this.now().subtract(1, 'week'), this.now().add(1, 'week')]
        }

        if (this.isMomentsAgo(date)) {
            return 'Moments ago'
        }

        const [start, end] = relativePeriod

        return date.isBetween(start, end, null, '[]')
            ? this.ago(date)
            : this.formatDateTime(date)
    }

    /**
   * Format a date relative to now if within the given period,
   * otherwise use the date-only format.
   *
   * @param {import('dayjs').Dayjs} date
   * @param {[import('dayjs').Dayjs, import('dayjs').Dayjs]|null} [relativePeriod=null]
   * @returns {string}
   */
    formatDateRelative(date, relativePeriod = null) {
        if (!relativePeriod) {
            relativePeriod = [this.now().subtract(1, 'week'), this.now().add(1, 'week')]
        }

        if (this.isMomentsAgo(date)) {
            return 'Moments ago'
        }

        const [start, end] = relativePeriod

        return date.isBetween(start, end, null, '[]')
            ? this.ago(date)
            : this.formatDate(date)
    }

    /** @returns {DateHelper} */
    #clone() {
        const clone = new DateHelper(this.#locale, this.#appTimezone, this.#deviceTimezone)
        clone.#dayFormat = this.#dayFormat
        clone.#dateFormat = this.#dateFormat
        clone.#timeFormat = this.#timeFormat
        clone.#yearFormat = this.#yearFormat
        clone.#appendYearSymbol = this.#appendYearSymbol
        return clone
    }
}

